import { useState, useEffect } from 'react';
import {
  GithubIcon,
  LinkedinIcon,
  MailIcon,
  Code2Icon,
  DatabaseIcon,
  TerminalIcon,
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BriefcaseIcon,
  CalendarIcon,
  MapPinIcon,
  AwardIcon,
  UsersIcon,
  ZapIcon,
  ArrowRightIcon,
  DownloadIcon,
  ExternalLinkIcon,
} from 'lucide-react';

// Project type definition
type Project = {
  id: number;
  title: string;
  description: string;
  coverImage: string;
  galleryImages: string[];
};

type Experience = {
  id: number;
  title: string;
  company: string;
  location: string;
  startDate: string;
  endDate: string;
  description: string[];
  technologies: string[];
};

// Project data
const projects: Project[] = [
  {
    id: 1,
    title: "Miya-Stock – Logiciel de Gestion de Stock Complète",
    description: `Miya-Stock est une solution complète de gestion de stock destinée aux boutiques, magasins, pharmacies et petites entreprises. 
    Elle offre un suivi précis des produits, des ventes, des achats, de la gestion des utilisateurs avec droits différenciés, des rapports analytiques et des inventaires.
    Développée avec Laravel, elle est disponible en version web et desktop pour une flexibilité maximale.
    `,
    coverImage: "/images/stock/3.png",
    galleryImages: [
      "/images/stock/1.png",
      "/images/stock/2.png",
      "/images/stock/3.png"
    ]
  },
  {
    id: 2,
    title: "EasyFacture – Application de Gestion de Factures",
    description: `EasyFacture est une application de gestion simplifiée de factures pour les ventes sans réapprovisionnement.
    Elle permet la création rapidement de factures, la gestion des clients, le suivi des paiements et la génération de rapports.
    Interface intuitive et ergonomique pour une utilisation immédiate sans formation préalable.
    `,
    coverImage: "/images/easyFacture/1.png",
    galleryImages: [
      "/images/easyFacture/1.png",
      "/images/easyFacture/2.png",
      "/images/easyFacture/3.png"
    ]
  },
  {
    id: 3,
    title: "MiyaStation – Application de Gestion de Carburant",
    description: `MiyaStation est une solution spécialisée pour la gestion des stations-service et des points de vente de carburant.
    Elle gère les stocks de carburant, les ventes, les paiements, les statistiques de consommation par client, et la facturation automatique.
    Parfait pour les stations-service, les dépôts et les flottes d'entreprise.
    `,
    coverImage: "/images/station/1.png",
    galleryImages: [
      "/images/station/1.png",
      "/images/station/2.png"
    ]
  },
  {
    id: 4,
    title: "MiyaPointage – Application de Gestion de Présence",
    description: `MiyaPointage est un logiciel de gestion de la présence et des congés dans les entreprises.
    Elle permet le pointage des employés, la gestion des congés, des absences, des horaires de travail et la génération de rapports RH.
    Intégration avec les systèmes de paie pour une synchronisation automatique des données.
    `,
    coverImage: "/images/pointage/1.png",
    galleryImages: [
      "/images/pointage/1.png"
    ]
  },
  {
    id: 5,
    title: "Gui360 – ERP de Transport et Logistique",
    description: `Gui360 est une solution ERP complète destinée aux entreprises de transport et de logistique.
    Elle intègre les modules de gestion des ressources humaines (RH), finance, qualité hygiène et sécurité environnement (QHSE), gestion des véhicules et suivi des trajets.
    Plateforme d'entreprise centralisée pour une meilleure coordination opérationnelle et une visibilité globale des opérations.
    `,
    coverImage: "/images/gui360/4.png",
    galleryImages: [
      "/images/gui360/1.png",
      "/images/gui360/2.png",
      "/images/gui360/3.png"
    ]
  }
];

const experiences: Experience[] = [
  {
    id: 1,
    title: "Développeur Full Stack",
    company: "Global Itec",
    location: "Conakry, Guinée",
    startDate: "21 Avril 2025",
    endDate: "En cours",
    description: [
      "Conception et développement de solutions digitales complètes pour collectivités locales et entreprises",
      "Développement d'applications web modernes avec React et frameworks backend",
      "Intégration d'APIs tierces et services externes",
      "Tests unitaires et documentation technique",
      "Support client et maintenance des solutions en production"
    ],
    technologies: ["React", "Laravel", "Node.js", "MySQL", "TypeScript", "Git"]
  },
  {
    id: 2,
    title: "Développeur Freelance",
    company: "Freelance",
    location: "Conakry, Guinée",
    startDate: "2024",
    endDate: "Avril 2025",
    description: [
      "Développement de projets web et desktop personnalisés pour divers clients",
      "Conception et implémentation de solutions logicielles complètes",
      "Gestion de projets de bout en bout avec les clients",
      "Développement des 5 solutions propriétaires (Miya-Stock, EasyFacture, MiyaStation, MiyaPointage, Gui360)"
    ],
    technologies: ["React", "Node.js", "Laravel", "Electron", "MySQL", "PostgreSQL"]
  }
];

function App() {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const openGallery = (project: Project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const closeGallery = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === selectedProject.galleryImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedProject.galleryImages.length - 1 : prev - 1
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/20 backdrop-blur-lg border-b border-white/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold text-white">MYB</div>
            <div className="hidden md:flex space-x-8">
              <a href="#about" className="text-white/80 hover:text-white transition-colors">À Propos</a>
              <a href="#skills" className="text-white/80 hover:text-white transition-colors">Compétences</a>
              <a href="#projects" className="text-white/80 hover:text-white transition-colors">Projets</a>
              <a href="#experience" className="text-white/80 hover:text-white transition-colors">Expérience</a>
              <a href="#contact" className="text-white/80 hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-600/10 to-purple-600/10 rounded-full blur-3xl"></div>
        </div>

        <div className={`container mx-auto px-6 text-center relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Profile Image */}
          <div className="relative mb-8 inline-block">
            <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-4 border-white/20 shadow-2xl mx-auto relative">
              <img
                src="/images/profil.jpg"
                alt="Photo de profil"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <ZapIcon size={16} className="text-white" />
            </div>
          </div>

          {/* Main Title */}
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Développeur
            </span>
            <br />
            <span className="text-white">Full Stack</span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-blue-200 mb-4 max-w-3xl mx-auto">
            Créateur de Solutions Logicielles Innovantes
          </p>

          {/* Stats */}
          <div className="flex justify-center gap-8 mb-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">5+</div>
              <div className="text-blue-300 text-sm">Solutions Propriétaires</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">1+</div>
              <div className="text-blue-300 text-sm">Années d'Expérience</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">100%</div>
              <div className="text-blue-300 text-sm">Projets Réussis</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a
              href="#projects"
              className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 transform hover:scale-105"
            >
              Découvrir mes Projets
              <ArrowRightIcon size={20} className="inline ml-2 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#contact"
              className="px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
            >
              Me Contacter
            </a>
          </div>

          {/* Social Links */}
          <div className="flex justify-center space-x-6">
            <a
              href="https://github.com/BARRY-2025/profil_developper"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-110"
            >
              <GithubIcon size={24} className="text-white" />
            </a>
            <a
              href="https://www.linkedin.com/in/mamadou-yaya-barry-6a973a399"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-110"
            >
              <LinkedinIcon size={24} className="text-white" />
            </a>
            <a
              href="mailto:yayaclnmamadou22@gmail.com"
              className="p-3 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-110"
            >
              <MailIcon size={24} className="text-white" />
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gradient-to-b from-slate-900 to-slate-800 relative">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                À Propos de Moi
              </h2>
              <p className="text-xl text-blue-200 max-w-3xl mx-auto">
                Développeur passionné spécialisé dans la création de solutions logicielles complètes
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Text */}
              <div className="space-y-6">
                <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10">
                  <h3 className="text-2xl font-bold text-white mb-4">Mon Parcours</h3>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    Après avoir obtenu ma licence professionnelle, j'ai commencé ma carrière en freelance en développant
                    des solutions personnalisées pour divers clients. Depuis avril 2025, je travaille chez Global Itec
                    où je conçois et développe des applications web et desktop complètes.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Aujourd'hui, je suis fier d'avoir créé 5 solutions propriétaires qui répondent aux besoins
                    spécifiques de différents secteurs d'activité.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 backdrop-blur-lg rounded-xl p-6 border border-white/10">
                    <AwardIcon className="w-8 h-8 text-blue-400 mb-3" />
                    <h4 className="text-lg font-semibold text-white mb-2">Expertise</h4>
                    <p className="text-gray-300 text-sm">Full Stack Development</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 backdrop-blur-lg rounded-xl p-6 border border-white/10">
                    <UsersIcon className="w-8 h-8 text-purple-400 mb-3" />
                    <h4 className="text-lg font-semibold text-white mb-2">Clients</h4>
                    <p className="text-gray-300 text-sm">Entreprises & Particuliers</p>
                  </div>
                </div>
              </div>

              {/* Right Column - Skills Preview */}
              <div className="space-y-6">
                <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10">
                  <h3 className="text-2xl font-bold text-white mb-6">Technologies Maîtresses</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { name: 'React', level: 'Expert' },
                      { name: 'Node.js', level: 'Expert' },
                      { name: 'Laravel', level: 'Avancé' },
                      { name: 'TypeScript', level: 'Avancé' },
                      { name: 'MySQL', level: 'Expert' },
                      { name: 'Git', level: 'Expert' }
                    ].map((tech, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-gray-300">{tech.name}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          tech.level === 'Expert' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {tech.level}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 bg-gradient-to-b from-slate-800 to-slate-900 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Compétences Techniques
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              Technologies et outils que je maîtrise pour créer des solutions exceptionnelles
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="group bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/10">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Code2Icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Frontend</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Création d'interfaces utilisateur modernes, responsives et performantes
              </p>
              <div className="space-y-2">
                {['React', 'TypeScript', 'HTML5', 'CSS3', 'Tailwind CSS'].map((tech) => (
                  <div key={tech} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span className="text-gray-300">{tech}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="group bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/10">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <DatabaseIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Backend & Données</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Développement d'APIs robustes et gestion efficace des données
              </p>
              <div className="space-y-2">
                {['Node.js', 'Laravel', 'MySQL', 'PostgreSQL', 'APIs REST'].map((tech) => (
                  <div key={tech} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span className="text-gray-300">{tech}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="group bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-green-500/10">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <TerminalIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Outils & DevOps</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Automatisation et déploiement de solutions scalables
              </p>
              <div className="space-y-2">
                {['Git', 'Docker', 'CI/CD', 'Linux', 'Electron', 'GitHub Actions'].map((tech) => (
                  <div key={tech} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-gray-300">{tech}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 bg-gradient-to-b from-slate-900 to-slate-800 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Expériences Professionnelles
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              Mon parcours professionnel et les projets sur lesquels j'ai travaillé
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {experiences.map((exp, index) => (
              <div key={exp.id} className="relative mb-12">
                {/* Timeline line */}
                {index !== experiences.length - 1 && (
                  <div className="absolute left-8 top-20 bottom-0 w-1 bg-gradient-to-b from-blue-500/50 to-purple-500/50"></div>
                )}

                <div className="flex gap-8">
                  {/* Timeline dot */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white shadow-lg border-4 border-slate-800">
                      <BriefcaseIcon size={24} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-300 group">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-white group-hover:text-blue-300 transition-colors duration-300">{exp.title}</h3>
                        <p className="text-xl text-blue-400 font-semibold mt-2">{exp.company}</p>
                      </div>
                      <div className="mt-4 md:mt-0 flex items-center gap-4 text-gray-400">
                        <div className="flex items-center gap-2">
                          <MapPinIcon size={16} />
                          <span className="text-sm">{exp.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CalendarIcon size={16} />
                          <span className="text-sm">{exp.startDate} - {exp.endDate}</span>
                        </div>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {exp.description.map((item, i) => (
                        <li key={i} className="flex gap-3 text-gray-300">
                          <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex flex-wrap gap-3">
                      {exp.technologies.map((tech, i) => (
                        <span key={i} className="px-4 py-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 rounded-full text-sm font-medium border border-blue-500/30 hover:border-blue-400/50 transition-colors duration-300">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 bg-gradient-to-b from-slate-800 to-slate-900 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Mes Solutions Propriétaires
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              Découvrez les 5 solutions logicielles que j'ai développées pour répondre aux besoins spécifiques de différents secteurs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {projects.map((project, index) => (
              <div
                key={project.id}
                className="group relative bg-white/5 backdrop-blur-lg rounded-2xl overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Project Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.coverImage}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                  {/* Overlay with icon */}
                  <div className="absolute top-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ExternalLinkIcon size={20} className="text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed mb-4 line-clamp-3">
                    {project.description}
                  </p>

                  {/* Action Button */}
                  <button
                    onClick={() => openGallery(project)}
                    className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <span>Voir le Projet</span>
                    <ArrowRightIcon size={18} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>

                {/* Hover effect border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            ))}
          </div>

          {/* Call to action */}
          <div className="text-center mt-16">
            <p className="text-gray-300 mb-6">
              Intéressé par une collaboration ou souhaitez en savoir plus sur mes solutions ?
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 transform hover:scale-105"
            >
              <span>Discutons de Votre Projet</span>
              <ArrowRightIcon size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-slate-900 to-black relative">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Prêt à Collaborer ?
            </h2>
            <p className="text-xl text-blue-200 mb-12 max-w-2xl mx-auto">
              Je suis disponible pour des projets intéressants et des opportunités de collaboration.
              Contactez-moi pour discuter de vos besoins en solutions logicielles innovantes.
            </p>

            {/* Contact Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <a
                href="mailto:yayaclnmamadou22@gmail.com"
                className="group bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-blue-400/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/10"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <MailIcon size={28} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Email</h3>
                <p className="text-gray-300 text-sm">yayaclnmamadou22@gmail.com</p>
              </a>

              <a
                href="https://www.linkedin.com/in/mamadou-yaya-barry-6a973a399"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-blue-400/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/10"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <LinkedinIcon size={28} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">LinkedIn</h3>
                <p className="text-gray-300 text-sm">Mamadou Yaya Barry</p>
              </a>

              <a
                href="https://github.com/BARRY-2025/profil_developper"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-blue-400/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/10"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <GithubIcon size={28} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">GitHub</h3>
                <p className="text-gray-300 text-sm">BARRY-2025</p>
              </a>
            </div>

            {/* CTA Button */}
            <div className="mb-12">
              <a
                href="mailto:yayaclnmamadou22@gmail.com?subject=Collaboration - Développement Logiciel"
                className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white font-bold text-lg rounded-full hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 transform hover:scale-105 animate-pulse hover:animate-none"
              >
                <MailIcon size={24} />
                <span>Démarrer un Projet</span>
                <ArrowRightIcon size={24} />
              </a>
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 pt-8">
              <p className="text-gray-400 text-sm mb-4">
                © 2026 Développeur Logiciel Professionnel. Tous droits réservés.
              </p>
              <p className="text-gray-500 text-xs">
                Conakry, Guinée 🇬🇳
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative w-full max-w-4xl mx-4">
            {/* Close button */}
            <button
              onClick={closeGallery}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2 transition-all hover:bg-opacity-75"
            >
              <XIcon size={24} />
            </button>

            {/* Image container */}
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              <img
                src={selectedProject.galleryImages[currentImageIndex]}
                alt={`${selectedProject.title} - Image ${currentImageIndex + 1}`}
                className="w-full h-full object-contain"
              />

              {/* Navigation buttons */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-75 transition-opacity"
              >
                <ChevronLeftIcon size={24} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-75 transition-opacity"
              >
                <ChevronRightIcon size={24} />
              </button>

              {/* Image counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 px-3 py-1 rounded-full text-white text-sm">
                {currentImageIndex + 1} / {selectedProject.galleryImages.length}
              </div>
            </div>

            {/* Project title and description */}
            <div className="mt-4 text-white text-center">
              <h3 className="text-2xl font-bold mb-2">{selectedProject.title}</h3>
              <p className="text-gray-300">{selectedProject.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;